import React, { useEffect, useState } from "react";
import { ShieldCheck, Users, Trash, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");
  const [testPreview, setTestPreview] = useState(null);
  const [busyCleanup, setBusyCleanup] = useState(false);

  const loadStats = () => {
    api.get("/admin/stats").then(r => setStats(r.data)).catch(e => setErr(e?.response?.data?.detail || "Unauthorized"));
  };
  const loadPreview = () => {
    api.get("/admin/test-users/preview").then(r => setTestPreview(r.data)).catch(() => {});
  };

  useEffect(() => { loadStats(); loadPreview(); }, []);

  const cleanupTestUsers = async () => {
    if (!window.confirm(`Permanently delete ${testPreview?.matched_count || 0} test users + cascade their projects, scripts, videos, payouts? This cannot be undone.`)) return;
    setBusyCleanup(true);
    try {
      const { data } = await api.post("/admin/test-users/cleanup");
      const cascaded = Object.entries(data.cascaded || {}).map(([k, v]) => `${v} ${k}`).join(", ");
      toast.success(`Deleted ${data.deleted} users${cascaded ? ` + ${cascaded}` : ""}`);
      loadStats(); loadPreview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally { setBusyCleanup(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="admin-page">
      <p className="text-[#FF3B6B] text-sm font-mono">// ADMIN</p>
      <h1 className="text-3xl md:text-4xl font-bold tracking-tighter mt-2 flex items-center gap-3">
        <ShieldCheck className="text-[#FF3B6B]" /> Control center
      </h1>

      {err && <div className="mt-6 glass rounded-xl p-6 text-white/60">{err}</div>}

      {stats && (
        <>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { k: "users", l: "Users", v: stats.total_users },
              { k: "projects", l: "Projects", v: stats.total_projects },
              { k: "scripts", l: "Scripts", v: stats.total_scripts },
              { k: "mrr", l: "MRR", v: `$${stats.mrr_estimate}`, accent: true },
            ].map((s) => (
              <div key={s.k} className="glass rounded-xl p-5" data-testid={`admin-stat-${s.k}`}>
                <p className="text-xs text-white/50 uppercase tracking-wider">{s.l}</p>
                <p className={`mt-2 font-mono text-2xl ${s.accent ? "text-[#FF3B6B]" : ""}`}>{s.v}</p>
              </div>
            ))}
          </div>

          {/* Test user cleanup panel */}
          <div className="mt-6 glass-strong rounded-xl p-6" data-testid="test-cleanup-panel">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-semibold flex items-center gap-2"><Trash className="text-[#EAB308]" /> Test user cleanup</h2>
                <p className="text-sm text-white/50 mt-1">
                  Remove automation accounts that match: <code className="text-xs bg-white/5 px-1.5 py-0.5 rounded">@example.com</code>,{" "}
                  <code className="text-xs bg-white/5 px-1.5 py-0.5 rounded">test_*</code>,{" "}
                  <code className="text-xs bg-white/5 px-1.5 py-0.5 rounded">playwright*</code>, etc. Cascades to projects/scripts/videos/payouts.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={loadPreview}
                  className="glass px-3 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-white/5"
                  data-testid="test-cleanup-refresh"
                >
                  <Eye size={14} /> Refresh
                </button>
                <button
                  onClick={cleanupTestUsers}
                  disabled={busyCleanup || (testPreview?.matched_count ?? 0) === 0}
                  className="bg-[#EAB308] text-black px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-40 hover:bg-[#EAB308]/90"
                  data-testid="test-cleanup-btn"
                >
                  {busyCleanup ? "Deleting…" : `Delete ${testPreview?.matched_count ?? 0} test users`}
                </button>
              </div>
            </div>
            {testPreview && testPreview.sample.length > 0 && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                {testPreview.sample.map((u) => (
                  <div key={u.email} className="text-xs text-white/60 font-mono px-3 py-1.5 rounded bg-white/5" data-testid={`test-cleanup-row-${u.email}`}>
                    {u.email}
                  </div>
                ))}
                {testPreview.matched_count > testPreview.sample.length && (
                  <div className="text-xs text-white/40 px-3 py-1.5">
                    …and {testPreview.matched_count - testPreview.sample.length} more
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 glass rounded-xl p-6">
            <h2 className="font-semibold mb-4 flex items-center gap-2"><Users className="text-[#00E5FF]" /> Recent users</h2>
            <div className="space-y-2">
              {stats.recent_users.map((u) => (
                <div key={u.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10" data-testid={`admin-user-${u.id}`}>
                  <div>
                    <p className="font-medium text-sm">{u.name}</p>
                    <p className="text-xs text-white/50">{u.email}</p>
                  </div>
                  <span className="text-xs uppercase tracking-wider text-[#FF3B6B]">{u.plan}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
